import React, { useRef, useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ServicesLinks } from '../data/servicesData';
import { FaArrowRight } from 'react-icons/fa';
import WebPlatformsDetail from './WebPlatformsDetail';
import MobileAppsDetail from './MobileAppsDetail';
import SaaSDetail from './SaaSDetail';
import EmailDetail from './EmailDetail';
import AIDetail from './AIDetail';
import DevOpsDetail from './DevOpsDetail';





/* ── Intersection Observer hook ─────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function ServiceDetail() {
  const { serviceName } = useParams();
  const [heroRef, heroVisible] = useInView(0.1);
  const [benefitsRef, benefitsVisible] = useInView(0.1);
  const [processRef, processVisible] = useInView(0.1);

  // Find the specific service data based on the URL parameter
  const service = ServicesLinks.find(s => s.id === serviceName);

  // If service not found, redirect to error
  if (!service) {
    return <Navigate to="/error" replace />;
  }

  // Custom visual page for Web Platforms
  if (serviceName === 'web-development') {
    return <WebPlatformsDetail />;
  }

  // Custom visual page for Native Mobile Apps
  if (serviceName === 'mobile-app-development') {
    return <MobileAppsDetail />;
  }

  // Custom visual page for SaaS Solutions
  if (serviceName === 'ecommerce-solutions') {
    return <SaaSDetail />;
  }

  // Custom visual page for Email Hosting Services
  if (serviceName === 'email-services') {
    return <EmailDetail />;
  }

  // Custom visual page for AI Integration & Agents
  if (serviceName === 'ai-intelligent-agents') {
    return <AIDetail />;
  }

  // Custom visual page for DevOps & Cloud Scaling
  if (serviceName === 'devops-scaling') {
    return <DevOpsDetail />;
  }





  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION (SPLIT PANE) ───────────────────────────── */}
      <section ref={heroRef} className="pt-32 pb-20 px-6 relative overflow-hidden"
               style={{ 
                 background: 'var(--bg-card)',
                 borderBottom: '1px solid var(--section-divider)',
               }}>
        
        {/* Background Decorative Blur */}
        <div className={`absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] opacity-10 pointer-events-none 
                         bg-gradient-to-br ${service.gradient}`} />

        <div className="section-container relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Text */}
          <div className={`lg:w-1/2 anim-hidden ${heroVisible ? 'anim-visible' : ''}`}>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full shadow-sm mb-6"
                 style={{ 
                   background: 'var(--accent-soft)',
                   border: '1px solid var(--border-color)',
                 }}>
                <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.gradient}`} />
               <span className="text-xs font-bold uppercase tracking-wider"
                     style={{ color: 'var(--text-tertiary)' }}>Service Detail</span>
            </div>
            
            <h1 className="display-md mb-6 leading-[1.1]" style={{ color: 'var(--text-primary)' }}>
              {service.title}
            </h1>
            
            <p className="text-lg leading-relaxed mb-8 max-w-xl" style={{ color: 'var(--text-secondary)' }}>
              {service.description}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contactus" className="btn-primary inline-flex">
                Start a Project <FaArrowRight size={11} />
              </Link>
            </div>
          </div>
          
          {/* Right Image */}
          <div className={`lg:w-1/2 w-full flex justify-center anim-hidden anim-delay-2 ${heroVisible ? 'anim-visible' : ''}`}>
             <div className="relative w-full max-w-[500px] aspect-square rounded-[2rem] overflow-hidden shadow-2xl"
                  style={{ border: '1px solid var(--border-color)' }}>
                <div className={`absolute inset-0 opacity-20 bg-gradient-to-tr ${service.gradient} mix-blend-overlay z-10`} />
                <img 
                  src={process.env.PUBLIC_URL + service.imageName} 
                  alt={service.title} 
                  className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                />
             </div>
          </div>
        </div>
      </section>

      {/* ── BENTO GRID BENEFITS ─────────────────────────────────── */}
      <section ref={benefitsRef} className="section-padding" style={{ background: 'var(--bg-surface)' }}>
        <div className="section-container">
          <div className={`text-center mb-16 anim-hidden ${benefitsVisible ? 'anim-visible' : ''}`}>
             <p className="eyebrow mb-3">Key Features</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Why Choose This Service</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Our comprehensive approach ensures every aspect of your project is engineered for performance and scalability.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
               {service.benefits && service.benefits.map((benefit, i) => (
                 <div 
                    key={i} 
                    className={`cyber-card rounded-3xl p-8 card-hover
                                anim-hidden anim-delay-${Math.min(i + 1, 6)} ${benefitsVisible ? 'anim-visible' : ''}`}
                    style={{ border: '1px solid var(--border-color)' }}
                 >
                   <div className={`w-12 h-12 rounded-2xl ${service.iconBg} flex items-center justify-center mb-6`}>
                     <benefit.icon size={20} className="opacity-90" />
                   </div>
                   <h3 className="text-lg font-bold mb-3 tracking-tight"
                       style={{ color: 'var(--text-primary)' }}>{benefit.title}</h3>
                   <p className="text-sm leading-relaxed"
                      style={{ color: 'var(--text-secondary)' }}>{benefit.text}</p>
                 </div>
               ))}
          </div>
        </div>
      </section>

      {/* ── ANIMATED PROCESS TIMELINE ───────────────────────────── */}
      <section ref={processRef} className="section-padding"
               style={{ 
                 background: 'var(--bg-card)',
                 borderTop: '1px solid var(--section-divider)',
                 borderBottom: '1px solid var(--section-divider)',
               }}>
        <div className="section-container max-w-4xl mx-auto">
          <div className={`text-center mb-20 anim-hidden ${processVisible ? 'anim-visible' : ''}`}>
             <p className="eyebrow mb-3">Methodology</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>How We Work</h2>
          </div>

          <div className="relative">
             {/* Vertical Line */}
             <div className="absolute left-[27px] md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2 hidden sm:block"
                  style={{ background: 'var(--section-divider)' }} />

             <div className="space-y-12">
               {service.processSteps && service.processSteps.map((step, i) => (
                 <div 
                   key={i} 
                   className={`relative flex flex-col md:flex-row items-start md:items-center justify-between group
                               anim-hidden anim-delay-${i + 1} ${processVisible ? 'anim-visible' : ''}`}
                 >
                    <div className="md:w-[45%] text-left md:text-right pr-0 md:pr-12 mb-4 md:mb-0 order-2 md:order-1 ml-16 md:ml-0 mt-2 md:mt-0">
                       <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{step.title}</h3>
                       <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
                    </div>

                    <div className="absolute left-0 md:left-1/2 w-14 h-14 rounded-full flex items-center justify-center
                                    md:-translate-x-1/2 z-10 group-hover:shadow-md transition-all duration-300"
                         style={{ 
                           background: 'var(--bg-card)',
                           border: '1px solid var(--border-color)',
                         }}>
                       <span className={`text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r ${service.gradient}`}>
                         0{i + 1}
                       </span>
                    </div>

                    <div className="md:w-[45%] pl-0 md:pl-12 order-3 md:order-3 invisible md:visible" />
                 </div>
               ))}
             </div>
          </div>
        </div>
      </section>

      {/* ── BACK CTA ────────────────────────────────────────────── */}
      <section className="py-20 text-center" style={{ background: 'var(--bg-primary)' }}>
         <Link to="/services" className="text-sm font-semibold transition-colors inline-flex items-center gap-2"
               style={{ color: 'var(--accent)' }}>
            <FaArrowRight size={12} className="transform rotate-180" /> Back to All Services
         </Link>
      </section>

    </div>
  );
}
